import { apiClient } from './client';
import {
  Product,
  ProductImageMeta,
  ProductPart,
  ProductPartPayload,
  ProductPayload,
} from '../types/product';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchProducts(keyword?: string): Promise<Product[]> {
  const { data } = await apiClient.get<Product[]>('/products', {
    params: keyword?.trim() ? { q: keyword.trim() } : {},
  });
  return data;
}

export async function fetchNextProductCode(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/products/suggestions/product-code'
  );
  return data;
}

export async function fetchProduct(productId: number): Promise<Product> {
  const { data } = await apiClient.get<Product>(`/products/${productId}`);
  return data;
}

export async function createProduct(payload: ProductPayload): Promise<Product> {
  const { data } = await apiClient.post<Product>('/products', payload);
  return data;
}

export async function updateProduct(productId: number, payload: ProductPayload): Promise<Product> {
  const { data } = await apiClient.patch<Product>(`/products/${productId}`, payload);
  return data;
}

export async function deleteProduct(productId: number): Promise<void> {
  await apiClient.delete(`/products/${productId}`);
}

export async function fetchProductImages(productId: number): Promise<ProductImageMeta[]> {
  const { data } = await apiClient.get<ProductImageMeta[]>(`/products/${productId}/images`);
  return data;
}

export async function uploadProductImages(productId: number, images: File[]): Promise<Product> {
  const formData = new FormData();
  images.forEach((file) => formData.append('images', file));
  const { data } = await apiClient.post<Product>(`/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** @deprecated use uploadProductImages — primary 기준 하위호환 */
export async function uploadProductImage(productId: number, image: File): Promise<Product> {
  const formData = new FormData();
  formData.append('image', image);
  const { data } = await apiClient.post<Product>(`/products/${productId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteProductImageById(productId: number, imageId: number): Promise<void> {
  await apiClient.delete(`/products/${productId}/images/${imageId}`);
}

/** @deprecated primary 삭제 — 하위호환 */
export async function deleteProductImage(productId: number): Promise<void> {
  await apiClient.delete(`/products/${productId}/image`);
}

export async function setPrimaryProductImage(
  productId: number,
  imageId: number
): Promise<Product> {
  const { data } = await apiClient.put<Product>(
    `/products/${productId}/images/${imageId}/primary`
  );
  return data;
}

export async function fetchProductImageByIdUrl(
  productId: number,
  imageId: number
): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/products/${productId}/images/${imageId}`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(data);
}

export async function fetchProductImageUrl(productId: number): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/products/${productId}/image`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(data);
}

export async function fetchProductThumbnailUrl(productId: number): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/products/${productId}/image/thumbnail`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(data);
}

export async function fetchProductParts(productId: number): Promise<ProductPart[]> {
  const { data } = await apiClient.get<ProductPart[]>(`/products/${productId}/parts`);
  return data;
}

export async function createProductPart(
  productId: number,
  payload: ProductPartPayload
): Promise<ProductPart> {
  const { data } = await apiClient.post<ProductPart>(`/products/${productId}/parts`, payload);
  return data;
}

export async function updateProductPart(
  productId: number,
  partId: number,
  payload: ProductPartPayload
): Promise<ProductPart> {
  const { data } = await apiClient.patch<ProductPart>(
    `/products/${productId}/parts/${partId}`,
    payload
  );
  return data;
}

export async function deleteProductPart(productId: number, partId: number): Promise<void> {
  await apiClient.delete(`/products/${productId}/parts/${partId}`);
}
